import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Clock, Send, Target, Flag, Wrench, CalendarClock, BookOpen, CheckCircle2, Circle } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import DashShell from "@/components/dash/DashShell";
import NotificationBell from "@/components/dash/NotificationBell";
import { OP_LINKS } from "./OperatorDashboard";
import { Card, StatusPill, btnPrimary, btnGhost, fieldCls } from "@/components/portal/ui";

const DIAG_SECTIONS = [
  ["current_sleep_picture", "Current Sleep Picture"], ["last_night_timeline", "Last-Night Timeline"],
  ["top_priority_areas", "Top Priority Areas"], ["what_appears_connected", "What Appears Connected"],
  ["what_may_not_need_changing", "What May Not Need Changing Yet"], ["professional_observations", "Professional Observations"],
  ["what_to_address_first", "What to Address First"], ["next_step_options", "Next-Step Options"],
];
const BP_SECTIONS = [
  ["current_sleep_picture", "Current Sleep Picture"], ["goals", "Goals"],
  ["daily_rhythm", "Personalized Daily Rhythm"], ["wake_guidance", "Wake Period Guidance"],
  ["nap_strategy", "Nap Plan"], ["bedtime_plan", "Bedtime Plan"], ["settling_strategy", "Settling Strategy"],
  ["night_waking_plan", "Night-Waking Strategy"], ["implementation_sequence", "Sequence of Changes"],
  ["days_1_3", "Days 1–3"], ["days_4_7", "Days 4–7"], ["monitor", "What to Watch"],
  ["troubleshooting", "Troubleshooting"], ["stay_consistent", "When to Stay Consistent"],
  ["when_adjust", "When to Adjust"], ["when_ask_help", "When to Ask for Help"],
  ["change_first", "What to Change First"], ["leave_alone", "What to Leave Alone"], ["custom_notes", "Custom Notes"],
];
const INHOME_SECTIONS = [
  ["overnight_observations", "Overnight Observations"], ["morning_debrief", "Morning Debrief"],
  ["recommended_adjustments", "Recommended Adjustments"], ["followup_summary", "Follow-Up Summary"],
  ["next_steps", "Next-Step Guidance"],
];
const SNAP_CATS = ["Nap Consistency", "Bedtime Pattern", "Night-Waking Pattern", "Settling Pattern", "Schedule Alignment", "Sleep Environment", "Routine Consistency"];
const SNAP_STATUSES = ["Stable", "Variable", "Needs Review", "Priority Area"];

const CONCERN_LABELS = {
  constant_waking: "Constant night waking", short_naps: "Short naps (20–30 min)", crib: "Won't sleep in the crib",
  bedtime: "Bedtime takes forever", regression: "Sudden change / regression", unsure: "Not sure what's wrong",
};
const CLARITY_LABELS = {
  age_months: "Baby's age (months)", primary_concern: "Primary concern", bedtime: "Current bedtime",
  falls_asleep: "Falls asleep by", wakings: "Night wakings", resolve: "What resolves a waking",
  naps: "Naps per day", nap_length: "Typical nap length", sleeps_where: "Sleeps best", bedtime_len: "Bedtime length",
  changed_when: "When things changed", environment: "Sleep environment", tried: "Already tried",
  goals: "Parent goal", medical: "Health / feeding / breathing notes",
};
const BP_LABELS = {
  typical_day: "Typical daily schedule", wake_windows: "Wake windows", nap_detail: "Nap pattern",
  bedtime_routine: "Bedtime routine", sleep_onset: "Sleep onset", settling_method: "Settling method",
  night_detail: "Night-waking pattern", night_feeds: "Night feeds", environment_detail: "Sleep environment",
  family_routines: "Family routines", childcare: "Childcare / daycare", developmental: "Developmental changes",
  strategies_tried: "Prior strategies", implementation_pref: "Implementation preferences",
  comfortable_changing: "Comfortable changing", not_changing: "Won't change", goals_constraints: "Goals & constraints",
};
const val = (v) => (v === "" || v == null ? null : String(v));

function fmtVal(k, v) {
  if (k === "primary_concern") return CONCERN_LABELS[v] || v;
  return val(v);
}
function Row({ label, value }) {
  if (value == null) return null;
  return <div className="flex justify-between gap-4 border-b border-[#F0ECE4] py-1.5 last:border-0"><span className="text-[#6B665E] text-xs">{label}</span><span className="text-[#1A1918] text-sm text-right whitespace-pre-wrap">{value}</span></div>;
}
function Group({ title, children, flag }) {
  const has = Array.isArray(children) ? children.some(Boolean) : children;
  if (!has) return null;
  return (
    <details className="border border-[#E5E1D8] rounded-lg bg-white group" open>
      <summary className={`cursor-pointer select-none px-4 py-2.5 text-sm font-medium flex items-center justify-between ${flag ? "text-[#9E4F4F]" : "text-[#1A1918]"}`}>
        <span className="flex items-center gap-1.5">{flag && <AlertTriangle size={13} />}{title}</span>
        <span className="text-[#8A998C] text-xs group-open:rotate-90 transition-transform">▸</span>
      </summary>
      <div className="px-4 pb-3 pt-1">{children}</div>
    </details>
  );
}

function ClarityReview({ intake, flags }) {
  const a = intake?.answers || {};
  const tl = intake?.timeline || {};
  if (!intake || Object.keys(a).length === 0) return <Card className="p-6" data-testid="op-clarity-review"><div className="eyebrow text-[#8A998C] mb-2">Clarity intake</div><p className="text-[#6B665E] text-sm">No intake submitted yet.</p></Card>;
  const settling = [val(a.falls_asleep), a.resolve ? `resolves by ${a.resolve}` : null].filter(Boolean).join(" · ");
  const naps = [val(a.naps) && `${a.naps} naps`, val(a.nap_length)].filter(Boolean).join(" · ");
  const loc = [val(a.sleeps_where), val(a.environment)].filter(Boolean).join(" · ");
  const hasTimeline = tl.bedtime || (tl.wakings || []).length > 0 || tl.morning_wake;
  return (
    <div className="space-y-4" data-testid="op-clarity-review">
      <Card className="p-6 bg-[#9F634E]/5 border-[#9F634E]/20">
        <div className="eyebrow text-[#9F634E] mb-3">Clarity summary</div>
        <div className="grid sm:grid-cols-2 gap-x-6">
          <Row label="Primary concern" value={fmtVal("primary_concern", a.primary_concern)} />
          <Row label="Typical bedtime" value={val(a.bedtime)} />
          <Row label="Night wakings" value={val(a.wakings)} />
          <Row label="Settling" value={settling || null} />
          <Row label="Naps" value={naps || null} />
          <Row label="Sleep location" value={loc || null} />
          <Row label="Recent changes" value={val(a.changed_when)} />
          <Row label="Parent goal" value={val(a.goals)} />
        </div>
      </Card>
      <div className="space-y-2">
        <Group title="Last Night Timeline">{hasTimeline ? (
          <div className="text-sm space-y-1">
            {tl.bedtime && <Row label="Bedtime" value={tl.bedtime} />}
            {(tl.wakings || []).map((w, i) => <Row key={i} label={`Wake ${i + 1}`} value={`${w.time || "—"}${w.settle ? " — " + w.settle : ""}`} />)}
            {tl.morning_wake && <Row label="Morning wake" value={tl.morning_wake} />}
            {tl.typical && <Row label="Typical night?" value={tl.typical} />}
            {tl.hardest && <Row label="Hardest part" value={tl.hardest} />}
          </div>) : null}</Group>
        <Group title="Bedtime & Settling">{[<Row key="1" label={CLARITY_LABELS.bedtime} value={val(a.bedtime)} />, <Row key="2" label={CLARITY_LABELS.bedtime_len} value={val(a.bedtime_len)} />, <Row key="3" label={CLARITY_LABELS.falls_asleep} value={val(a.falls_asleep)} />]}</Group>
        <Group title="Night Wakings">{[<Row key="1" label={CLARITY_LABELS.wakings} value={val(a.wakings)} />, <Row key="2" label={CLARITY_LABELS.resolve} value={val(a.resolve)} />]}</Group>
        <Group title="Naps">{[<Row key="1" label={CLARITY_LABELS.naps} value={val(a.naps)} />, <Row key="2" label={CLARITY_LABELS.nap_length} value={val(a.nap_length)} />]}</Group>
        <Group title="Environment">{[<Row key="1" label={CLARITY_LABELS.environment} value={val(a.environment)} />, <Row key="2" label={CLARITY_LABELS.sleeps_where} value={val(a.sleeps_where)} />]}</Group>
        <Group title="Recent Changes">{<Row label={CLARITY_LABELS.changed_when} value={val(a.changed_when)} />}</Group>
        <Group title="Parent Goals">{<Row label={CLARITY_LABELS.goals} value={val(a.goals)} />}</Group>
        <Group title="Previous Attempts">{<Row label={CLARITY_LABELS.tried} value={val(a.tried)} />}</Group>
        <Group title="Safety Flags" flag={(flags || []).length > 0 || !!val(a.medical)}>{[
          (flags || []).length > 0 ? <Row key="f" label="Flagged terms" value={flags.join(", ")} /> : null,
          val(a.medical) ? <Row key="m" label={CLARITY_LABELS.medical} value={val(a.medical)} /> : null,
        ]}</Group>
        <RawIntake a={a} labels={CLARITY_LABELS} />
      </div>
    </div>
  );
}

function RawIntake({ a, labels }) {
  const entries = Object.entries(a || {}).filter(([, v]) => v !== "" && v != null);
  if (entries.length === 0) return null;
  return (
    <details className="border border-[#E5E1D8] rounded-lg bg-white group">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-[#6B665E] flex items-center justify-between">
        <span>Full Raw Intake</span><span className="text-[#8A998C] text-xs group-open:rotate-90 transition-transform">▸</span>
      </summary>
      <div className="px-4 pb-3 pt-1" data-testid="op-raw-intake">
        {entries.map(([k, v]) => <Row key={k} label={(labels && labels[k]) || k.replace(/_/g, " ")} value={String(v)} />)}
      </div>
    </details>
  );
}

function ClarityReference({ intake, flags }) {
  const a = intake?.answers || {};
  return (
    <details className="border border-[#E5E1D8] rounded-lg bg-white group" data-testid="bp-clarity-reference">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-[#1A1918] flex items-center justify-between">
        <span>Reference: Original Clarity Intake</span>
        <span className="text-[#8A998C] text-xs group-open:rotate-90 transition-transform">▸</span>
      </summary>
      <div className="px-4 pb-4 pt-1">
        {Object.keys(a).length === 0
          ? <p className="text-[#6B665E] text-sm">No Clarity intake answers on record.</p>
          : <ClarityReview intake={intake} flags={flags} />}
      </div>
    </details>
  );
}

function BlueprintReview({ intake }) {
  const a = intake?.blueprint_answers || {};
  if (Object.keys(a).length === 0) return <Card className="p-5"><div className="eyebrow text-[#8A998C] mb-1">Blueprint intake</div><p className="text-[#6B665E] text-sm">Blueprint intake not submitted yet.</p></Card>;
  const groups = [
    ["Goals & Constraints", ["goals_constraints", "comfortable_changing", "not_changing"]],
    ["Daily Schedule & Wake Windows", ["typical_day", "wake_windows"]],
    ["Naps", ["nap_detail"]],
    ["Bedtime & Settling", ["bedtime_routine", "sleep_onset", "settling_method"]],
    ["Night Wakings & Feeds", ["night_detail", "night_feeds"]],
    ["Environment", ["environment_detail"]],
    ["Family & Childcare", ["family_routines", "childcare", "developmental"]],
    ["Prior Strategies & Preferences", ["strategies_tried", "implementation_pref"]],
  ];
  return (
    <Card className="p-5" data-testid="op-blueprint-review">
      <div className="eyebrow text-[#9F634E] mb-3">Blueprint intake — for reference</div>
      <div className="space-y-2">
        {groups.map(([title, keys]) => (
          <Group key={title} title={title}>{keys.map((k) => <Row key={k} label={BP_LABELS[k] || k} value={val(a[k])} />)}</Group>
        ))}
        <RawIntake a={a} labels={BP_LABELS} />
      </div>
    </Card>
  );
}

function NotesPanel({ customerId, notes, onSaved }) {
  const [text, setText] = useState("");
  const add = async () => { if (!text.trim()) return; await http.post("/operator/notes", { customer_id: customerId, text }); setText(""); toast.success("Note saved (private)"); onSaved(); };
  const del = async (nid) => { await http.delete(`/operator/notes/${nid}`); toast.success("Note deleted"); onSaved(); };
  return (
    <div className="max-w-2xl space-y-5">
      <Card className="p-6">
        <div className="eyebrow text-[#9F634E] mb-2">Private consultant note</div>
        <p className="text-[#8A998C] text-xs mb-3">Only visible to consultants & the owner — never shown to the parent.</p>
        <textarea rows={3} data-testid="consultant-note-text" className={fieldCls} placeholder="Observations, plan reasoning, reminders…" value={text} onChange={(e) => setText(e.target.value)} />
        <button onClick={add} data-testid="consultant-note-add" className={`${btnPrimary} mt-3`}>Save note</button>
      </Card>
      <Card className="p-6">
        <div className="eyebrow text-[#8A998C] mb-3">Notes history</div>
        {(notes || []).length === 0 ? <p className="text-[#6B665E] text-sm">No notes yet.</p> : (
          <div className="space-y-3" data-testid="consultant-notes-list">
            {notes.map((n) => (
              <div key={n.id} className="border-b border-[#F0ECE4] pb-3 last:border-0">
                <div className="flex items-center justify-between text-xs text-[#8A998C] mb-1">
                  <span>{n.author_name || "Consultant"} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
                  <button onClick={() => del(n.id)} data-testid={`note-del-${n.id}`} className="text-[#9E4F4F] hover:underline">Delete</button>
                </div>
                <p className="text-[#1A1918] text-sm whitespace-pre-wrap">{n.text}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const ADMIN_PKGS = [
  { key: "diagnostic", label: "My Clarity", price: 99 },
  { key: "blueprint", label: "Personalized Sleep Blueprint", price: 279 },
  { key: "guided", label: "Guided Virtual Consulting", price: 749 },
  { key: "inhome_overnight", label: "In-Home Overnight", price: 2549 },
];

function PackageControls({ customerId, services, onSaved }) {
  const [busy, setBusy] = useState("");
  const [reasonFor, setReasonFor] = useState("");
  const [reasonText, setReasonText] = useState("");
  const svcOf = (key) => services.find((s) => s.product_key === key);
  const set = async (product_key, status, reason) => {
    setBusy(product_key);
    try {
      await http.post(`/admin/customer/${customerId}/package`, { product_key, status, reason: reason || "" });
      toast.success("Package updated");
      setReasonFor(""); setReasonText("");
      onSaved();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update package"); }
    finally { setBusy(""); }
  };
  const statusOf = (key) => {
    const svc = svcOf(key);
    if (!svc) return { status: "unpaid", source: null };
    return { status: svc.billing_status || "paid", source: svc.access_source || "stripe" };
  };
  const onClickOpt = (key, val) => {
    if (val === "complimentary") { setReasonFor(key); setReasonText(svcOf(key)?.comp_reason || ""); }
    else { setReasonFor(""); set(key, val); }
  };
  const OPTIONS = [["unpaid", "Unpaid"], ["paid", "Paid"], ["complimentary", "Complimentary"]];
  return (
    <Card className="p-5 mb-6 border-[#9F634E]/20" data-testid="admin-package-controls">
      <div className="eyebrow text-[#9F634E] mb-1">Package access &amp; billing · Admin</div>
      <p className="text-[#8A998C] text-xs mb-4">Marking a package Paid or Complimentary unlocks it and its intake &amp; modules without any Stripe charge or Stripe revenue.</p>
      <div className="space-y-3">
        {ADMIN_PKGS.map((p) => {
          const { status, source } = statusOf(p.key);
          const svc = svcOf(p.key);
          const badge = status === "complimentary" ? "Complimentary"
            : status === "paid" ? (source === "stripe" ? "Paid · Stripe" : "Paid · Manual") : "Unpaid";
          const badgeCls = status === "complimentary" ? "bg-[#667A66]/15 text-[#4d6b4d]"
            : status === "paid" ? "bg-[#C49A5A]/15 text-[#8a6a2e]" : "bg-[#F3EFE9] text-[#8A998C]";
          return (
            <div key={p.key} className="border-b border-[#F0ECE4] pb-3 last:border-0 last:pb-0" data-testid={`pkg-row-${p.key}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <p className="text-[#1A1918] text-sm font-medium">{p.label} <span className="text-[#8A998C] font-normal">· ${p.price}</span></p>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full inline-block mt-1 ${badgeCls}`} data-testid={`pkg-badge-${p.key}`}>{badge}</span>
                  {status === "complimentary" && svc?.comp_reason && (
                    <p className="text-[#8A998C] text-xs mt-1 italic" data-testid={`pkg-reason-${p.key}`}>“{svc.comp_reason}”</p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {OPTIONS.map(([val, lbl]) => (
                    <button key={val} disabled={busy === p.key || (status === val && val !== "complimentary")} onClick={() => onClickOpt(p.key, val)}
                      data-testid={`pkg-${p.key}-${val}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors disabled:cursor-default ${status === val ? "bg-[#9F634E] text-white border-[#9F634E]" : "border-[#E5E1D8] text-[#6B665E] hover:border-[#9F634E]/50"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              {reasonFor === p.key && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid={`pkg-reason-form-${p.key}`}>
                  <input value={reasonText} onChange={(e) => setReasonText(e.target.value)} maxLength={300}
                    placeholder="Why is this gifted? (optional) — e.g. goodwill, friend of Andrea, service recovery"
                    data-testid={`pkg-reason-input-${p.key}`} className={`${fieldCls} flex-1`} />
                  <button onClick={() => set(p.key, "complimentary", reasonText)} disabled={busy === p.key}
                    data-testid={`pkg-reason-confirm-${p.key}`} className={`${btnPrimary} shrink-0`}>Confirm complimentary</button>
                  <button onClick={() => { setReasonFor(""); setReasonText(""); }}
                    data-testid={`pkg-reason-cancel-${p.key}`} className="text-sm text-[#6B665E] px-2">Cancel</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function OperatorCustomer() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  useSeo({ title: "Customer — Operator", noindex: true });

  const load = () => http.get(`/operator/customer/${id}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!d) return <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("clients")}><p className="text-[#6B665E]">Loading…</p></DashShell>;

  const { customer, child, intake, services, reports, sleep_logs, timeline, time_entries, messages, consultant_notes, modules, access } = d;
  const flags = intake?.flags || [];
  const svcKeys = new Set(services.map((s) => s.product_key));

  return (
    <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("clients")} rightSlot={<NotificationBell />}>
      <button onClick={() => nav("/operator")} className="flex items-center gap-2 text-[#6B665E] text-sm mb-5 hover:text-[#1A1918]"><ArrowLeft size={16} /> Back to queues</button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[#1A1918]">{customer.name}</h1>
          <p className="text-[#6B665E] text-sm">{customer.email}{child ? ` · ${child.name}, ${child.age_months ?? "?"} mo` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">{services.map((s) => <StatusPill key={s.id} state={`${s.product_key}: ${s.state}`} />)}</div>
      </div>

      {flags.length > 0 && (
        <Card className="p-4 mb-6 border-[#9E4F4F]/40 bg-[#9E4F4F]/5">
          <div className="flex items-center gap-2 text-[#9E4F4F] font-medium text-sm mb-1"><AlertTriangle size={16} /> Safety flags detected</div>
          <p className="text-[#6B665E] text-sm">Flagged terms: {flags.join(", ")}. Do not progress automatically. Consider recommending pediatric referral.</p>
        </Card>
      )}

      {user?.role === "admin" && <PackageControls customerId={id} services={services} onSaved={load} />}

      <div className="flex gap-2 mb-6 border-b border-[#E5E1D8]">
        {[["overview", "Overview"], ["diagnostic", "Clarity Report"], ["blueprint", "Blueprint"],
          ...(svcKeys.has("guided") ? [["guided", "Guided"]] : []),
          ...(svcKeys.has("inhome_overnight") ? [["inhome", "In-Home"]] : []),
          ["messages", "Messages"], ["notes", "Notes"], ["time", "Time & Fulfillment"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`op-tab-${k}`}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-[#9F634E] text-[#9F634E]" : "border-transparent text-[#6B665E]"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <ClarityReview intake={intake} flags={flags} />
          <div className="space-y-5">
            <CompletedModules modules={modules} access={access} />
            <Card className="p-6">
              <div className="eyebrow text-[#8A998C] mb-3">Timeline</div>
              <div className="space-y-2 text-sm">{timeline.slice(0, 8).map((t) => (
                <div key={t.id} className="flex justify-between"><span className="text-[#1A1918]">{t.label}</span><span className="text-[#8A998C] text-xs">{new Date(t.date).toLocaleDateString()}</span></div>
              ))}</div>
            </Card>
            <Card className="p-6">
              <div className="eyebrow text-[#8A998C] mb-3">Recent sleep logs</div>
              {sleep_logs.length ? sleep_logs.slice(0, 5).map((l) => <div key={l.id} className="text-sm text-[#6B665E] border-b border-[#F0ECE4] py-1.5">{l.date}: {l.wakings ?? "–"} wakings, longest {l.longest_stretch || "–"}</div>) : <p className="text-[#6B665E] text-sm">No logs.</p>}
            </Card>
          </div>
        </div>
      )}

      {tab === "diagnostic" && (
        <div className="space-y-6">
          <div data-testid="op-original-clarity-intake">
            <div className="mb-3">
              <div className="eyebrow text-[#9F634E]">Original Clarity Intake</div>
              <p className="text-[#6B665E] text-sm">The parent's submitted answers. This source record stays here permanently — separate from the Clarity Report you write below — and remains visible after you publish.</p>
            </div>
            <ClarityReview intake={intake} flags={flags} />
          </div>
          <div className="border-t border-[#E5E1D8] pt-6">
            <div className="eyebrow text-[#8A998C] mb-3">Clarity Report — your written conclusions</div>
            <ReportEditor customerId={id} product="diagnostic" sections={DIAG_SECTIONS} existing={reports.find((r) => r.product === "diagnostic")} withSnapshot onSaved={load} />
          </div>
        </div>
      )}
      {tab === "blueprint" && (
        <div className="space-y-5">
          <ClarityReference intake={intake} flags={flags} />
          <BlueprintReview intake={intake} />
          <ReportEditor customerId={id} product="blueprint" sections={BP_SECTIONS} existing={reports.find((r) => r.product === "blueprint")} onSaved={load} />
        </div>
      )}
      {tab === "guided" && <GuidedPanel customerId={id} intake={intake} messages={(messages || []).filter((m) => m.product_key === "guided")} service={services.find((s) => s.product_key === "guided")} onSaved={load} />}
      {tab === "messages" && <MessagesPanel customerId={id} messages={(messages || []).filter((m) => m.product_key === "general")} onSaved={load} />}
      {tab === "inhome" && <InHomePanel customerId={id} intake={intake} service={services.find((s) => s.product_key === "inhome_overnight")} existing={reports.find((r) => r.product === "inhome_overnight")} onSaved={load} />}
      {tab === "notes" && <NotesPanel customerId={id} notes={consultant_notes} onSaved={load} />}
      {tab === "time" && <TimePanel customerId={id} entries={time_entries} services={services} onSaved={load} />}
    </DashShell>
  );
}

function CompletedModules({ modules, access }) {
  const m = modules || { completed: [], in_progress: [], completed_count: 0, unlocked_count: 0 };
  const hasAccess = access?.has_access;
  const comp = access?.complimentary;
  return (
    <Card className="p-6" data-testid="completed-modules-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 eyebrow text-[#8A998C]"><BookOpen size={14} /> Completed Modules</div>
        {hasAccess && (
          <span data-testid="module-access-badge"
            className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 border ${comp ? "bg-[#C49A5A]/15 text-[#8a6a2e] border-[#C49A5A]/40" : "bg-[#667A66]/12 text-[#4d6b4d] border-[#667A66]/30"}`}>
            {access?.label} access
          </span>
        )}
      </div>
      {!hasAccess ? (
        <p className="text-[#6B665E] text-sm" data-testid="module-no-access">No unlocked modules — this client has no paid or complimentary package yet.</p>
      ) : (
        <>
          <div className="text-sm text-[#1A1918] mb-3" data-testid="module-count">
            <span className="font-display text-xl">{m.completed_count}</span>
            <span className="text-[#8A998C]"> of {m.unlocked_count} unlocked modules completed</span>
          </div>
          {m.completed.length === 0 && m.in_progress.length === 0 ? (
            <p className="text-[#6B665E] text-sm">No modules started yet.</p>
          ) : (
            <div className="space-y-1.5">
              {m.completed.map((x) => (
                <div key={x.key} data-testid={`module-completed-${x.key}`} className="flex items-center justify-between gap-3 border-b border-[#F0ECE4] py-1.5">
                  <span className="flex items-center gap-2 text-sm text-[#1A1918]"><CheckCircle2 size={14} className="text-[#4d6b4d] shrink-0" /> {x.title}</span>
                  <span className="text-[#8A998C] text-xs shrink-0">{x.category}{x.at ? ` · ${new Date(x.at).toLocaleDateString()}` : ""}</span>
                </div>
              ))}
              {m.in_progress.map((x) => (
                <div key={x.key} data-testid={`module-inprogress-${x.key}`} className="flex items-center justify-between gap-3 border-b border-[#F0ECE4] py-1.5">
                  <span className="flex items-center gap-2 text-sm text-[#6B665E]"><Circle size={14} className="text-[#C49A5A] shrink-0" /> {x.title}</span>
                  <span className="text-[#8A998C] text-xs shrink-0">In progress</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function ReportEditor({ customerId, product, sections, existing, withSnapshot, onSaved }) {
  const published = existing && ["Delivered", "Viewed", "Completed"].includes(existing.status);
  const [secs, setSecs] = useState(existing?.sections || {});
  const [snap, setSnap] = useState(() => {
    const base = {}; SNAP_CATS.forEach((c) => base[c] = ""); (existing?.snapshot || []).forEach((s) => base[s.category] = s.status); return base;
  });
  const [clar, setClar] = useState("");

  const payload = () => ({ customer_id: customerId, product, sections: secs,
    snapshot: withSnapshot ? SNAP_CATS.filter((c) => snap[c]).map((c) => ({ category: c, status: snap[c] })) : [] });

  const saveDraft = async () => { await http.post("/operator/report", payload()); toast.success("Draft saved"); onSaved(); };
  const publish = async () => { await http.post("/operator/report/publish", payload()); toast.success("Published to customer"); onSaved(); };
  const clarify = async () => { if (!clar) return; await http.post("/operator/clarify", { customer_id: customerId, product, message: clar }); toast.success("Clarification requested"); setClar(""); onSaved(); };

  if (published) return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-3"><StatusPill state={existing.status} /><span className="text-[#6B665E] text-sm">Published {existing.published_at && new Date(existing.published_at).toLocaleDateString()}</span></div>
      <p className="text-[#1A1918]">This {product === "diagnostic" ? "Clarity Report" : product === "blueprint" ? "Blueprint" : "follow-up"} has been delivered to the customer.</p>
    </Card>
  );

  return (
    <div className="space-y-5">
      <Card className="p-4 bg-[#9F634E]/5 border-[#9F634E]/20">
        <p className="text-sm text-[#6B665E]">You write every personalized conclusion. The platform never generates recommendations. Use approved educational blocks as needed, then save a draft or publish.</p>
      </Card>
      {withSnapshot && (
        <Card className="p-6">
          <div className="eyebrow text-[#9F634E] mb-4">Sleep Pattern Snapshot</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {SNAP_CATS.map((c) => (
              <div key={c} className="flex items-center justify-between gap-3">
                <span className="text-[#1A1918] text-sm">{c}</span>
                <select className="bg-[#F9F8F6] border border-[#E5E1D8] rounded-lg px-3 py-1.5 text-sm" value={snap[c]} onChange={(e) => setSnap((s) => ({ ...s, [c]: e.target.value }))}>
                  <option value="">—</option>{SNAP_STATUSES.map((st) => <option key={st}>{st}</option>)}
                </select>
              </div>
            ))}
          </div>
        </Card>
      )}
      {sections.map(([k, label]) => (
        <Card key={k} className="p-5">
          <label className="eyebrow text-[#9F634E] mb-2 block">{label}</label>
          <textarea rows={3} data-testid={`report-${k}`} className={fieldCls} value={secs[k] || ""} onChange={(e) => setSecs((s) => ({ ...s, [k]: e.target.value }))} />
        </Card>
      ))}
      <Card className="p-5">
        <label className="eyebrow text-[#8A998C] mb-2 block">Request clarification instead</label>
        <div className="flex gap-3">
          <input className={fieldCls} placeholder="What do you need from the parent?" value={clar} onChange={(e) => setClar(e.target.value)} />
          <button onClick={clarify} className={btnGhost}>Request</button>
        </div>
      </Card>
      <div className="flex gap-3">
        <button data-testid="report-save-draft" onClick={saveDraft} className={btnGhost}>Save draft</button>
        <button data-testid="report-publish" onClick={publish} className={btnPrimary}>Complete & publish</button>
      </div>
    </div>
  );
}

function AnswerBlock({ title, answers }) {
  const entries = Object.entries(answers || {}).filter(([, v]) => v !== "" && v != null);
  return (
    <Card className="p-6">
      <div className="eyebrow text-[#8A998C] mb-3">{title}</div>
      {entries.length === 0 ? <p className="text-[#6B665E] text-sm">Not submitted yet.</p> : (
        <div className="space-y-2 text-sm">
          {entries.map(([k, v]) => (
            <div key={k} className="py-1.5 border-b border-[#F0ECE4] last:border-0">
              <div className="text-[#6B665E] text-xs mb-0.5">{k.replace(/_/g, " ")}</div>
              <div className="text-[#1A1918] whitespace-pre-wrap">{String(v)}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MessagesPanel({ customerId, messages, onSaved }) {
  const [reply, setReply] = useState("");
  const send = async () => {
    if (!reply.trim()) return;
    await http.post("/operator/message", { customer_id: customerId, product: "general", text: reply });
    setReply(""); toast.success("Reply sent"); onSaved();
  };
  return (
    <Card className="p-6" data-testid="op-messages-panel">
      <div className="eyebrow text-[#9F634E] mb-4">Messages with the client</div>
      <div className="space-y-3 mb-4 max-h-80 overflow-y-auto" data-testid="op-general-messages">
        {(messages || []).length === 0 ? <p className="text-[#6B665E] text-sm">No messages yet.</p> :
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "consultant" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.sender === "consultant" ? "bg-[#9F634E] text-white" : "bg-[#F3EFE9] text-[#1A1918]"}`}>
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                <div className={`text-[10px] mt-1 ${m.sender === "consultant" ? "text-white/70" : "text-[#8A998C]"}`}>
                  {m.sender === "consultant" ? "You" : "Client"} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                </div>
              </div>
            </div>
          ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="op-general-reply" className={fieldCls} placeholder="Reply to the client…" value={reply}
          onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
        <button data-testid="op-general-send" onClick={send} className="bg-[#9F634E] text-white rounded-lg px-4 text-sm shrink-0">Send</button>
      </div>
    </Card>
  );
}

function GuidedPanel({ customerId, intake, messages, service, onSaved }) {
  const [reply, setReply] = useState("");
  const [note, setNote] = useState({ kind: "recommendation", text: "" });
  const guidance = service?.guidance || [];
  const send = async () => { if (!reply.trim()) return; await http.post("/operator/message", { customer_id: customerId, product: "guided", text: reply }); setReply(""); toast.success("Reply sent"); onSaved(); };
  const addNote = async () => { if (!note.text.trim()) return; await http.post("/operator/guided/note", { customer_id: customerId, kind: note.kind, text: note.text }); setNote({ ...note, text: "" }); toast.success("Saved"); onSaved(); };
  const complete = async () => { await http.post("/operator/guided/complete", { customer_id: customerId, product: "guided", state: "Completed" }); toast.success("Guided support completed"); onSaved(); };

  return (
    <div className="space-y-5">
      <AnswerBlock title="Guided onboarding answers" answers={intake?.guided_answers} />
      {service?.current_focus && (
        <Card className="p-5 bg-[#9F634E]/5 border-[#9F634E]/20"><div className="eyebrow text-[#9F634E] mb-1 flex items-center gap-1.5"><Target size={13} /> Current focus</div><p className="text-[#1A1918] text-sm">{service.current_focus}</p></Card>
      )}
      <Card className="p-6">
        <div className="eyebrow text-[#9F634E] mb-4">Post guidance</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {["recommendation", "adjustment", "focus", "milestone"].map((k) => (
            <button key={k} onClick={() => setNote((n) => ({ ...n, kind: k }))} className={`px-3 py-1.5 rounded-full text-sm border capitalize ${note.kind === k ? "bg-[#9F634E] border-[#9F634E] text-white" : "border-[#E5E1D8] text-[#1A1918]"}`}>{k}</button>
          ))}
        </div>
        <textarea rows={3} data-testid="guided-note-text" className={fieldCls} placeholder={`Write a ${note.kind}…`} value={note.text} onChange={(e) => setNote((n) => ({ ...n, text: e.target.value }))} />
        <button onClick={addNote} data-testid="guided-note-add" className={`${btnPrimary} mt-3 flex items-center gap-1.5`}><Wrench size={14} /> Save guidance</button>
        {guidance.length > 0 && (
          <div className="mt-5 space-y-2">
            {guidance.map((g, i) => (
              <div key={i} className="text-sm border-b border-[#F0ECE4] py-2">
                <span className="text-[#8A998C] text-xs capitalize mr-2">{g.kind === "milestone" ? <Flag size={11} className="inline text-[#667A66]" /> : null} {g.kind}:</span>
                <span className="text-[#1A1918]">{g.text}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-6">
        <div className="eyebrow text-[#9F634E] mb-4">Messages with the parent</div>
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto" data-testid="op-guided-messages">
          {(messages || []).length === 0 ? <p className="text-[#6B665E] text-sm">No messages yet.</p> :
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "consultant" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "consultant" ? "bg-[#9F634E] text-white" : "bg-[#F3EFE9] text-[#1A1918]"}`}><p className="whitespace-pre-wrap">{m.text}</p></div>
              </div>
            ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="op-guided-reply" className={fieldCls} placeholder="Reply to the parent…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
          <button onClick={send} data-testid="op-guided-send" className={`${btnPrimary} shrink-0 flex items-center gap-1.5`}><Send size={15} /> Send</button>
        </div>
      </Card>
      <button onClick={complete} className={btnGhost}>Mark guided support complete</button>
    </div>
  );
}

function InHomePanel({ customerId, intake, service, existing, onSaved }) {
  const [sched, setSched] = useState({ scheduled_at: "", arrival_time: service?.arrival_time || "", visit_details: service?.visit_details || "" });
  const save = async () => {
    if (!sched.scheduled_at) { toast.error("Pick a date & time"); return; }
    await http.post("/operator/inhome/schedule", { customer_id: customerId, scheduled_at: new Date(sched.scheduled_at).toISOString(), arrival_time: sched.arrival_time, visit_details: sched.visit_details });
    toast.success("Scheduled — reminder set"); onSaved();
  };
  const cancel = async () => { await http.post("/operator/inhome/cancel", { customer_id: customerId }); toast.success("Visit canceled"); onSaved(); };
  return (
    <div className="space-y-5">
      <AnswerBlock title="In-Home + Overnight onboarding answers" answers={intake?.inhome_answers} />
      <Card className="p-6">
        <div className="eyebrow text-[#9F634E] mb-4 flex items-center gap-1.5"><CalendarClock size={14} /> Schedule the overnight</div>
        {service?.scheduled_date && (
          <p className="text-sm text-[#4d6b4d] mb-3">Currently scheduled: <span className="font-medium">{service.scheduled_date}</span>{service.arrival_time ? ` · arrival ${service.arrival_time}` : ""} · reminder {service.reminder_status || "—"}</p>
        )}
        <div className="space-y-3">
          <input type="datetime-local" data-testid="inhome-sched-date" className={fieldCls} value={sched.scheduled_at} onChange={(e) => setSched((s) => ({ ...s, scheduled_at: e.target.value }))} />
          <input data-testid="inhome-arrival" className={fieldCls} placeholder="Arrival time note (e.g. 8:00pm)" value={sched.arrival_time} onChange={(e) => setSched((s) => ({ ...s, arrival_time: e.target.value }))} />
          <textarea rows={2} className={fieldCls} placeholder="Visit details (what to expect)" value={sched.visit_details} onChange={(e) => setSched((s) => ({ ...s, visit_details: e.target.value }))} />
          <div className="flex gap-3">
            <button onClick={save} data-testid="inhome-sched-save" className={btnPrimary}>Confirm schedule</button>
            {service?.scheduled_date && <button onClick={cancel} data-testid="inhome-sched-cancel" className={btnGhost}>Cancel visit</button>}
          </div>
        </div>
      </Card>
      <div>
        <div className="eyebrow text-[#9F634E] mb-2">Follow-up deliverable (published after the overnight)</div>
        <ReportEditor customerId={customerId} product="inhome_overnight" sections={INHOME_SECTIONS} existing={existing} onSaved={onSaved} />
      </div>
    </div>
  );
}

function TimePanel({ customerId, entries, services, onSaved }) {
  const [form, setForm] = useState({ product_key: services[0]?.product_key || "diagnostic", task: "intake review", minutes: "" });
  const add = async () => {
    if (!form.minutes) return;
    await http.post("/operator/time", { customer_id: customerId, product_key: form.product_key, task: form.task, minutes: Number(form.minutes) });
    toast.success("Time logged"); setForm((f) => ({ ...f, minutes: "" })); onSaved();
  };
  const total = entries.reduce((s, e) => s + e.minutes, 0);
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card className="p-6 space-y-4">
        <div className="eyebrow text-[#9F634E]">Log operator time</div>
        <select className={fieldCls} value={form.product_key} onChange={(e) => setForm((f) => ({ ...f, product_key: e.target.value }))}>
          {services.map((s) => <option key={s.id} value={s.product_key}>{s.product_key}</option>)}
        </select>
        <select className={fieldCls} value={form.task} onChange={(e) => setForm((f) => ({ ...f, task: e.target.value }))}>
          {["intake review", "clarity creation", "blueprint creation", "guided communication", "plan adjustments", "overnight preparation", "overnight service", "post-service follow-up"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <input type="number" data-testid="time-minutes" className={fieldCls} placeholder="Minutes" value={form.minutes} onChange={(e) => setForm((f) => ({ ...f, minutes: e.target.value }))} />
        <button onClick={add} data-testid="time-add" className={btnPrimary}>Add time</button>
      </Card>
      <Card className="p-6">
        <div className="flex items-center gap-2 eyebrow text-[#8A998C] mb-3"><Clock size={14} /> Recorded time · {(total/60).toFixed(1)}h</div>
        {entries.length ? entries.map((e) => (
          <div key={e.id} className="flex justify-between text-sm border-b border-[#F0ECE4] py-2"><span className="text-[#1A1918]">{e.task}</span><span className="text-[#6B665E]">{e.minutes} min</span></div>
        )) : <p className="text-[#6B665E] text-sm">No time logged.</p>}
      </Card>
    </div>
  );
}
